import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface SpriteFrame {
  /** Horizontal offset of the frame in the sprite sheet. */
  x: number;
  /** Vertical offset of the frame in the sprite sheet. */
  y: number;
  /** Frame width in sprite-sheet pixels. */
  w: number;
  /** Frame height in sprite-sheet pixels. */
  h: number;
}

export interface SpriteSheetPlayerProps {
  /** Sprite sheet image URL. */
  spriteSheetSrc: string;
  /** Full sprite sheet width in pixels. */
  sheetWidth: number;
  /** Full sprite sheet height in pixels. */
  sheetHeight: number;
  /** Ordered animation frames from the sprite-sheet metadata. */
  frames: ReadonlyArray<SpriteFrame>;
  /** Frame duration while animating in milliseconds. */
  frameDurationMs?: number;
  /** Delay after each full loop in milliseconds. */
  loopDelayMs?: number;
  /** Playback mode for looping through frames. */
  playback?: 'forward' | 'pingpong';
  /** Rendered width in CSS pixels. */
  width?: number;
  /** Rendered height in CSS pixels. */
  height?: number;
  /** Extra classes for layout/shadows/transforms. */
  className?: string;
}

/**
 * Lightweight sprite-sheet animation renderer for UI icons.
 *
 * @example
 * ```tsx
 * <SpriteSheetPlayer
 *   spriteSheetSrc={sheet}
 *   sheetWidth={1600}
 *   sheetHeight={2000}
 *   frames={[{ x: 0, y: 0, w: 400, h: 400 }]}
 * />
 * ```
 */
export function SpriteSheetPlayer({
  spriteSheetSrc,
  sheetWidth,
  sheetHeight,
  frames,
  frameDurationMs = 100,
  loopDelayMs = 0,
  playback = 'forward',
  width,
  height,
  className,
}: SpriteSheetPlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const sequence = useMemo(() => {
    if (frames.length <= 1) return [0];
    if (playback === 'pingpong') {
      const reverse = Array.from({ length: Math.max(frames.length - 2, 0) }, (_, index) => frames.length - 2 - index);
      return [...frames.map((_, index) => index), ...reverse];
    }
    return frames.map((_, index) => index);
  }, [frames, playback]);

  const [step, setStep] = useState(0);
  const [imageLoadTick, setImageLoadTick] = useState(0);
  const hasLoopedRef = useRef(false);

  useEffect(() => {
    const image = new Image();
    image.src = spriteSheetSrc;
    imageRef.current = image;

    const notifyImageChange = () => setImageLoadTick((current) => current + 1);
    image.addEventListener('load', notifyImageChange);
    image.addEventListener('error', notifyImageChange);

    const animationFrameId = image.complete ? window.requestAnimationFrame(notifyImageChange) : null;

    return () => {
      if (animationFrameId !== null) window.cancelAnimationFrame(animationFrameId);
      image.removeEventListener('load', notifyImageChange);
      image.removeEventListener('error', notifyImageChange);
    };
  }, [spriteSheetSrc]);

  useEffect(() => {
    if (sequence.length <= 1) return undefined;

    const delay = step === 0 && hasLoopedRef.current && loopDelayMs > 0 ? loopDelayMs : frameDurationMs;

    const timeoutId = window.setTimeout(() => {
      setStep((currentStep) => {
        const nextStep = (currentStep + 1) % sequence.length;
        if (nextStep === 0) hasLoopedRef.current = true;
        return nextStep;
      });
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [frameDurationMs, loopDelayMs, sequence.length, step]);

  useEffect(() => {
    hasLoopedRef.current = false;
  }, [sequence]);

  const frame = frames.length > 0 ? frames[sequence[step] ?? 0] : null;
  const frameWidth = frame?.w ?? 1;
  const frameHeight = frame?.h ?? 1;
  const frameRatio = frameWidth / frameHeight;
  const renderedWidth = width ?? (height ? height * frameRatio : frameWidth);
  const renderedHeight = height ?? (width ? width / frameRatio : frameHeight);

  useEffect(() => {
    if (!frame) return;

    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !image.complete) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(renderedWidth * pixelRatio);
    canvas.height = Math.round(renderedHeight * pixelRatio);
    canvas.style.width = `${renderedWidth}px`;
    canvas.style.height = `${renderedHeight}px`;

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, renderedWidth, renderedHeight);

    const sourceWidth = Math.max(Math.min(frame.w, sheetWidth - frame.x), 0);
    const sourceHeight = Math.max(Math.min(frame.h, sheetHeight - frame.y), 0);
    if (sourceWidth === 0 || sourceHeight === 0) return;

    const containScale = Math.min(renderedWidth / frame.w, renderedHeight / frame.h);
    const drawWidth = frame.w * containScale;
    const drawHeight = frame.h * containScale;
    const drawX = (renderedWidth - drawWidth) / 2;
    const drawY = (renderedHeight - drawHeight) / 2;

    context.drawImage(image, frame.x, frame.y, sourceWidth, sourceHeight, drawX, drawY, drawWidth, drawHeight);
  }, [frame, imageLoadTick, renderedHeight, renderedWidth, sheetHeight, sheetWidth]);

  if (!frame) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn('shrink-0', className)}
      width={Math.round(renderedWidth)}
      height={Math.round(renderedHeight)}
    />
  );
}
